/**
 * §1.3 — the three regional pillar buttons.
 *
 * Anatomy, top to bottom: entity mark, code and live project-count badge;
 * display name at 11px uppercase tracked; secondary micro-copy at 12px; a
 * divider; then the value / pipeline / red-flag row.
 *
 * **The colour rule is structural here, not decorative.** CGS's identity
 * colour is gold, and §0.1 measures gold on white at 2.10:1. So the CGS card
 * is drawn on navy and its gold appears only against that navy; the other two
 * cards use their own colour as the surface. No gold value ever lands on a
 * light surface, which is what `npm run check:contrast` enforces.
 *
 * A pillar the session does not hold still renders — inert, greyed and
 * labelled — because a button that vanishes reads as a missing feature rather
 * than as a scope boundary.
 */
import type { PillarButton } from '../lib/api';
import { money } from '../lib/status';

interface Props {
  pillars: PillarButton[];
  onOpen: (code: string) => void;
  activeCode?: string | null;
}

export function PillarButtons({ pillars, onOpen, activeCode }: Props): JSX.Element {
  return (
    <section aria-labelledby="pillars-heading">
      <h2 id="pillars-heading" className="sr-only">
        Entities
      </h2>
      <div className="grid grid-cols-1 gap-3 sm:grid-cols-2 lg:grid-cols-3">
        {pillars.map((pillar) => (
          <PillarCard
            key={pillar.code}
            pillar={pillar}
            active={activeCode === pillar.code}
            onOpen={onOpen}
          />
        ))}
      </div>
    </section>
  );
}

function PillarCard({
  pillar,
  active,
  onOpen,
}: {
  pillar: PillarButton;
  active: boolean;
  onOpen: (code: string) => void;
}): JSX.Element {
  const value = money(pillar.activeContractValue);

  if (!pillar.granted) {
    return (
      <div className="card flex flex-col justify-between p-4 opacity-70">
        <div>
          <p className="micro-label text-text-secondary">{pillar.code}</p>
          <p className="mt-1 text-h3 font-semibold text-text-primary">{pillar.displayName}</p>
          <p className="mt-1 text-body text-text-secondary">{pillar.microCopy}</p>
        </div>
        <p className="mt-3 border-t border-border pt-3 text-micro text-text-secondary">
          Your account is not granted this entity.
        </p>
      </div>
    );
  }

  return (
    <button
      type="button"
      data-pillar={pillar.code}
      aria-current={active ? 'page' : undefined}
      onClick={() => onOpen(pillar.code)}
      className="pillar-card flex w-full flex-col items-stretch rounded-card p-4 text-left"
    >
      <span className="flex items-start justify-between gap-3">
        <span className="flex items-center gap-2">
          <span className="pillar-card__mark" aria-hidden="true">
            {pillar.code.slice(0, 1)}
          </span>
          <span className="pillar-card__code">{pillar.code}</span>
        </span>
        <span className="pillar-card__badge numeric">
          {pillar.projectCount}
          <span className="sr-only"> projects</span>
        </span>
      </span>

      <span className="pillar-card__name mt-3 block">{pillar.displayName}</span>
      <span className="pillar-card__micro mt-1 block">{pillar.microCopy}</span>
      <span className="pillar-card__secondary mt-1 block">{pillar.secondaryLine}</span>

      <span className="pillar-card__divider mt-3 block" aria-hidden="true" />

      <span className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1">
        <span className="pillar-card__stat">
          <span className="pillar-card__stat-label">Active</span>{' '}
          <span className="numeric pillar-card__stat-value">{pillar.activeCount}</span>
        </span>
        <span className="pillar-card__stat">
          <span className="pillar-card__stat-label">Value</span>{' '}
          <span className="numeric pillar-card__stat-value">
            {value ?? '\u2014'}
            {value ? null : <span className="sr-only"> not recorded</span>}
          </span>
        </span>
        <span className="pillar-card__stat">
          <span className="pillar-card__stat-label">Pipeline</span>{' '}
          <span className="numeric pillar-card__stat-value">{pillar.pipelineCount}</span>
        </span>
        {/*
          §1.3: the red-flag dot appears only when the count is above zero, and
          it is the only element inside pillar chrome permitted to use --danger.
        */}
        {pillar.redFlagCount > 0 ? (
          <span className="pillar-card__flag">
            <span aria-hidden="true" className="pillar-card__flag-dot" />
            <span className="numeric">{pillar.redFlagCount}</span>
            <span className="sr-only"> flagged projects</span>
          </span>
        ) : null}
      </span>

      {pillar.motto ? <span className="pillar-card__motto mt-2 block">{pillar.motto}</span> : null}
    </button>
  );
}
